import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { ContentControlledEditingDirective } from 'app/js/src/domain/content-control/editing-directive';
import {
  IContentControlManager
} from 'app/js/src/domain/content-control/manager';
import {
  IContentControlRepository
} from 'app/js/src/domain/content-control/repository';
import { ContentControlStudioBaseViewModel } from '../content-control-studio-base-view-model';
import { IStudioEventsRef } from 'app/js/src/domain/content-control/studio';

@Component({
  selector: "evrideo-content-control-studio-events",
  templateUrl: "./content-control-studio-events.component.html",
  styleUrl: "./content-control-studio-events.component.scss",
})
export class ContentControlStudioEventsComponent implements IStudioEventsRef {
  @ViewChild("eventsListRef") eventsListRef: ElementRef<HTMLDivElement>;
  
  @Input("one-frame") oneFrame: number;
  @Input("mediaId") mediaId: number | null | undefined= null;
  @Input("channelUid") channelUid: string | null | undefined= null;
  @Input("contentControlManager") contentControlManager: IContentControlManager;
  @Input("view-model") vm: ContentControlStudioBaseViewModel;
  @Input() events: ContentControlledEditingDirective[] = [];
  @Input("selected-event-id") selectedEventId: number;
  public orderedEvents = Array<Array<ContentControlledEditingDirective>>();
  public selectedArrangeEventsBy: Set<string> = new Set();
  public selectedEventIndex: number = 0;
  private gapInMs: number = 0;
  private lastDigitCount = 1;
  public openedNestedEvents: Set<number> = new Set<number>();
  private _firstLoadThumbnails = true;
  private _lastSyncedEventCount = 0;

  constructor(

    private _repository: IContentControlRepository,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  async ngOnInit() {
    this.setupEvents();

  }
  async ngOnChanges(changes: SimpleChanges) {
    if (
      changes.selectedEventId != undefined &&
      changes.selectedEventId.currentValue == -1
    ) {
      this.selectedEventIndex = -1;
    }


    this.setupEvents();

    const totalEvents = this.orderedEvents?.reduce((n, g) => n + g.length, 0) ?? 0;
    const shouldSyncThumbnails =
      this.orderedEvents.length > 0 &&
      (this._firstLoadThumbnails || totalEvents > this._lastSyncedEventCount);
    if (shouldSyncThumbnails) {
      this._firstLoadThumbnails = false;
      this._lastSyncedEventCount = totalEvents;
      await this._syncAsyncLoadThumbnails();
    }
  }

  setupEvents() {
    if (this.events && this.events.length > 0) {
      if (this.orderedEvents.length == 0 || this.updateEvents() == false) {
        this.selectedArrangeEventsBy.add("nested");
        this.orderedEvents = this.organizeTimelineEvents(this.events);
      }
      if (this.selectedEventId > -1) {
        const selectedEventArrayIndex = this.orderedEvents.findIndex((x) =>
          x.some((x) => x.id === this.selectedEventId),
        );
        if (
          selectedEventArrayIndex > -1 &&
          this.orderedEvents[selectedEventArrayIndex].length > 1
        ) {
          this.openedNestedEvents.add(selectedEventArrayIndex);
        }
        setTimeout(() => {
          const item = this.eventsListRef.nativeElement.querySelector(
            `[data-event-id="${this.selectedEventId}"]`,
          );
          if (item) {
            item.scrollIntoView({ behavior: "smooth" });
          }
        }, 0);
      }
    } else {
      this.orderedEvents = [];
    }
  }

  private async _syncAsyncLoadThumbnails(): Promise<void> {
    let contentControlItemWithThumbnails;
    if (this.channelUid) {
      contentControlItemWithThumbnails = await this._repository.getItemByChannelUid(this.channelUid);
    } else if (this.mediaId != null) {
      contentControlItemWithThumbnails = await this._repository.getItemByMediaId(this.mediaId, true);
    } else {
      return;
    }
    if (!contentControlItemWithThumbnails) {
      return;
    }
    const thumbnailsPerEventId: { [key: number]: { thumbnailStart:string, thumbnailEnd:string} } = {};

    for (let reportableEvent of contentControlItemWithThumbnails.reportableEvents) {
      thumbnailsPerEventId[reportableEvent.id] = { thumbnailStart: reportableEvent.startFrame, thumbnailEnd: reportableEvent.endFrame };
    }

    for (let editingDirective of contentControlItemWithThumbnails.editingDirectives) {
      thumbnailsPerEventId[editingDirective.id] = { thumbnailStart: editingDirective.startFrame, thumbnailEnd: editingDirective.endFrame };
    }

    for (const groupedOrderedEvents of this.orderedEvents) {
      for (const event of groupedOrderedEvents) {
        if (thumbnailsPerEventId[event.id]) {
          event.startFrame = thumbnailsPerEventId[event.id].thumbnailStart;
          event.endFrame = thumbnailsPerEventId[event.id].thumbnailEnd;
        }
      }
    }
  }
  private updateEvents(): boolean {
    const eventsIds = this.events.map((e) => e.id);
    const orderedEventsIds = this.orderedEvents.flatMap((x) =>
      x.map((e) => e.id),
    );
    for (let newItem of this.events) {
      let found = false;

      for (let i = 0; i < this.orderedEvents.length; i++) {
        for (let j = 0; j < this.orderedEvents[i].length; j++) {
          if (eventsIds.includes(this.orderedEvents[i][j].id) == false) {
            return false; // event deleted
          }
          if (this.orderedEvents[i][j].id === newItem.id) {
            if (
              this.orderedEvents[i][j].startsAtMs != newItem.startsAtMs ||
              this.orderedEvents[i][j].endsAtMs != newItem.endsAtMs
            ) {
              return false; // event updated
            }
            this.orderedEvents[i][j] = newItem; // Update the item in place
            found = true;
            break;
          }
        }
        if (found) break;
      }
      if (found == false) {
        return false; // new item
      }
    }
    for (let orderedList of this.orderedEvents) {
      const notFound = orderedList.filter(
        (x) => eventsIds.includes(x.id) == false,
      );
      if (notFound && notFound.length > 0) {
        return false;
      }
    }
    return true;
  }
  ArrangeByTypeClicked(arrangeBy: string) {
    if (this.selectedArrangeEventsBy.has(arrangeBy)) {
      this.selectedArrangeEventsBy.delete(arrangeBy);
    } else {
      this.selectedArrangeEventsBy.add(arrangeBy);
    }
    if (this.selectedArrangeEventsBy.size > 0) {
      this.orderedEvents = this.organizeTimelineEvents(this.events);
    }
  }
  calculateGap(event: Event, stop: boolean = false) {
    if (stop) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    const input = event.target as HTMLInputElement;
    let value = +input.value;
    if (value < 0) {
      value = 0;
    }
    this.gapInMs = this.oneFrame * 1000 * value;
    this.orderedEvents = this.organizeTimelineEvents(this.events);
    const numDigits = Math.abs(value).toString().length;
    if (numDigits != this.lastDigitCount) {
      this.lastDigitCount = numDigits;
      input.style.width = `${numDigits * 10 + 30}px`;
    }
  }
  areEventsAprroved(events: ContentControlledEditingDirective[]) {
    return events.every((e) => e.isApproved || e.isMitigated);
  }

  onRevertEventClicked(id: number) {
    this.vm.onRevertEventClicked(id);
  }
  onEventClicked(e: Event, id: number) {
    e.preventDefault();
    e.stopImmediatePropagation();
    this.selectedEventIndex = -1;
    this.vm.changeSelectedSegment(id);
  }
  onMultiEventsClicked(e: Event, index: number) {
    e.preventDefault();
    e.stopImmediatePropagation();
    this.selectedEventIndex = index;
    this.vm.onMultiEventsClicked(this.orderedEvents[index].map((e) => e.id));
  }
  process(img: string) {
    if (!img) return null;
    if (img.startsWith("data:")) {
      return img;
    }
    return `data:image/png;base64,${img}`;
  }
  toggleEvents(e: Event, parentId: number) {
    e.preventDefault();
    e.stopImmediatePropagation();
    this.doToggleEvents(parentId);
    if (this.openedNestedEvents.has(parentId)) {
      this.openedNestedEvents.delete(parentId);
    } else {
      this.openedNestedEvents.add(parentId);
    }
  }
  private doToggleEvents(parentId: number) {
    const element: HTMLElement = document.querySelector(
      `[data-events-list="${parentId}"]`,
    );
    if (element) {
      const svg = element.querySelector(".more-arrow");
      const ul = element.nextElementSibling;
      ul?.classList.toggle("hidden");
      svg?.classList.toggle("open");
    }
  }
  get arrangeByNested() {
    return this.selectedArrangeEventsBy.has("nested");
  }
  get arrangeByOverlapped() {
    return this.selectedArrangeEventsBy.has("overlapped");
  }
  get arrangeByGap() {
    return this.selectedArrangeEventsBy.has("gap");
  }

  timeFormatFunction(milliseconds: number, justMinutesAndSeconds?: boolean): string {
    return this.vm?.timeFormatFunction(milliseconds, justMinutesAndSeconds) ?? '--:--:--.---';
  }

  multiEventsTimeRange(events: ContentControlledEditingDirective[]) {
    const startsAtMsArray = events.map((e) => e.startsAtMs);
    const endsAtMsArray = events.map((e) => e.endsAtMs);
    return `${this.timeFormatFunction(Math.min(...startsAtMsArray))} - ${this.timeFormatFunction(Math.max(...endsAtMsArray))}`;
  }
  getMultiEventsStartFrame(events: ContentControlledEditingDirective[]) {
    return this.process(events[0].startFrame);
  }
  getMultiEventsEndFrame(events: ContentControlledEditingDirective[]) {
    return this.process(events[events.length - 1].endFrame);
  }
  private organizeTimelineEvents(
    events: ContentControlledEditingDirective[],
  ): ContentControlledEditingDirective[][] {
    // Sort events by start time
    const sortedEvents = events
      .slice()
      .sort((a, b) => a.startsAtMs - b.startsAtMs);

    const organizedEvents: ContentControlledEditingDirective[][] = [];

    for (const event of sortedEvents) {
      let placed = false;

      for (const group of organizedEvents) {
        const lastEvent = group[group.length - 1];

        if (
          this.isNested(event, group)
          // ||
          // this.isOverlapping(event, lastEvent) ||
          // this.isWithinGap(event, lastEvent, this.gapInMs)
        ) {
          group.push(event);
          placed = true;
          break;
        }
      }

      if (!placed) {
        // Create a new group if the event doesn't fit into any existing group
        organizedEvents.push([event]);
      }
    }

    // Sort groups by the start time of their first event
    organizedEvents.sort((a, b) => a[0].startsAtMs - b[0].startsAtMs);

    return organizedEvents;
  }
  private isNested(
    event: ContentControlledEditingDirective,
    group: ContentControlledEditingDirective[],
  ): boolean {
    return (
      this.selectedArrangeEventsBy.has("nested") &&
      group.some(
        (existingEvent) =>
          existingEvent.action === event.action &&
          event.startsAtMs >= existingEvent.startsAtMs &&
          event.endsAtMs <= existingEvent.endsAtMs &&
          event !== existingEvent,
      )
    );
  }

  // private isOverlapping(
  //   event: ContentControlledEditingDirective,
  //   lastEvent: ContentControlledEditingDirective,
  // ): boolean {
  //   return (
  //     this.selectedArrangeEventsBy.has("overlapped") &&
  //     event.startsAtMs < lastEvent.endsAtMs
  //   );
  // }

  // private isWithinGap(
  //   event: ContentControlledEditingDirective,
  //   lastEvent: ContentControlledEditingDirective,
  //   maxGapMs: number,
  // ): boolean {
  //   return (
  //     this.selectedArrangeEventsBy.has("gap") &&
  //     event.startsAtMs - lastEvent.endsAtMs <= maxGapMs
  //   );
  // }
  onEventVisible(segments: ContentControlledEditingDirective[], isVisible: boolean) {
    if (isVisible && segments?.length) {
      for (const segment of segments) {
        const screenShotDirectives = segment.getScreenshotDirectives();
        for (const screenShotDirective of screenShotDirectives) {
          this.contentControlManager.screenshotQueue.next(screenShotDirective);
        }
      }
    }
  }
}
